/**
 * Logique pure de la machine à états des demandes de rôle.
 * Extraite de RoleRequestPage pour être testable sans React.
 */

export const RENOUNCE_WINDOW_DAYS   = 7;
export const RENOUNCE_COOLDOWN_DAYS = 15;
export const MAX_RENOUNCE_COUNT     = 3;

/**
 * Retourne la demande active (PENDING ou APPROVED) qui bloque une nouvelle soumission.
 * Un APPROVED antérieur à la dernière renonciation est ignoré (orphelin).
 */
export function computeBlockedFor(requests, role) {
  const candidate = requests.find(
    (r) => r.requested_role === role && ['PENDING', 'APPROVED'].includes(r.status),
  );
  if (!candidate || candidate.status !== 'APPROVED') return candidate ?? null;

  const latestRenounce = requests
    .filter((r) => r.requested_role === role && r.status === 'RENOUNCED')
    .sort((a, b) => new Date(b.renounced_at || b.updated_at) - new Date(a.renounced_at || a.updated_at))[0];

  if (latestRenounce) {
    const renounceTime = new Date(latestRenounce.renounced_at || latestRenounce.updated_at).getTime();
    const approveTime  = new Date(candidate.reviewed_at).getTime();
    if (renounceTime > approveTime) return null; // APPROVED obsolète
  }
  return candidate;
}

/**
 * Retourne { inCooldown, daysLeft, renounceCount, blocked } par rôle,
 * ou undefined si aucune renonciation trouvée.
 */
export function computeCooldownFor(requests, roles) {
  const now = Date.now();
  const result = {};
  for (const role of roles) {
    const renounced = requests
      .filter((r) => r.requested_role === role && r.status === 'RENOUNCED' && r.renounced_at)
      .sort((a, b) => new Date(b.renounced_at) - new Date(a.renounced_at))[0];
    const renounceCount = requests.filter(
      (r) => r.requested_role === role && r.status === 'RENOUNCED',
    ).length;
    if (renounced) {
      const daysSince = Math.floor((now - new Date(renounced.renounced_at)) / 86400000);
      const daysLeft  = Math.max(0, RENOUNCE_COOLDOWN_DAYS - daysSince);
      result[role] = {
        inCooldown: daysLeft > 0,
        daysLeft,
        renounceCount,
        blocked: renounceCount >= MAX_RENOUNCE_COUNT,
      };
    }
  }
  return result;
}

/**
 * Retourne { eligible, daysAgo, daysLeft } par rôle détenu.
 * eligible = true si l'approbation date de moins de RENOUNCE_WINDOW_DAYS.
 */
export function computeRenounceEligibility(requests, userRoles) {
  const now = Date.now();
  const result = {};
  for (const role of userRoles) {
    const approved = requests
      .filter((r) => r.requested_role === role && r.status === 'APPROVED' && r.reviewed_at)
      .sort((a, b) => new Date(b.reviewed_at) - new Date(a.reviewed_at))[0];
    if (approved) {
      const daysAgo = Math.floor((now - new Date(approved.reviewed_at)) / 86400000);
      result[role] = {
        eligible: daysAgo <= RENOUNCE_WINDOW_DAYS,
        daysAgo,
        daysLeft: Math.max(0, RENOUNCE_WINDOW_DAYS - daysAgo),
      };
    }
  }
  return result;
}
